import React, { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { Environment } from "components/Environment/utils/types";
import {
  canRequestEnvironmentStop,
  getEnvironmentStatus,
  getEnvironmentStatusLabel,
  getGpuCountFromEnvironment,
  getGpuNameFromEnvironment,
  getMemoryFromEnvironment,
  getVcpuFromEnvironment,
  isEnvironmentDeleteBlockedByStatus,
  normalizeEnvironmentStatus,
  parseTimestamp,
} from "components/Environment/utils/environtmentUtils";
import { EnvironmentStatus } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import EnvironmentCostPanel from "components/Environment/List/EnvironmentCostPanel";
import { DeleteConfirmModal } from "components/Environment/modals/DeleteEnvironmentConfirmModal";
import { StopModal } from "components/Environment/modals/StopModal";
import { useStorageCostPerHr } from "hooks/environment/useStorageCostPerHr";
import Modal from "components/Modals/Modal";
import { STORAGE_KEYS } from "constants/localStorage";
import { WorkspaceStatus } from "models/Workspace.model";
import { isOrgAdminOrMaintainerRole } from "utils/roleUtils";
import { useEnvironmentNetworkAccess } from "hooks/environment/useEnvironmentNetworkAccess";
import type { DisplayPort } from "utils/environment/networkAccessFallback";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "components/Graphs/Dropdown";
import { toast } from "utils/toast";
import { useLaunchableQuery } from "server/brevdeploy/launchables/queries";
import EnvironmentStatusStepper, {
  getEnvironmentSetupSteps,
} from "components/Environment/shared/EnvironmentStatusStepper";
import { EditNetworkModeDialog } from "./EditNetworkModeDialog";
import {
  ComputeCardTemplate,
  ComputeCardMenu,
  SSH_NOT_ENABLED_REASON,
  type ComputeCardMetadata,
} from "./ComputeCardTemplate";
import {
  ComputeCardKindLabel,
  ComputeCardOwner,
  ComputeCardRelativeTime,
  ComputeNetworkMode,
} from "./ComputeCardMetadata";
import { CloudComputeStatusSummary } from "components/Environment/shared/ComputeStatusSummary";
import { resolveNetworkMemberType } from "components/Environment/Networks/networkMode";
import { EnvironmentApplicationChip } from "./EnvironmentApplicationChip";
import {
  ComputeHardwareInlineSummary,
  ComputeHardwareSummary,
} from "./ComputeHardwareSummary";
import { ShareSecureLinkDialog } from "components/NodeDetail/BrevConnect/NodeSecureLinksSection";
import { canShareSecureLinkForCompute } from "./secureLinkSharingPermissions";
import { getCurrentUserAccessIdentities } from "components/Environment/utils/secureLinkAccess";
import {
  formatGpuVramFromByteValue,
  formatGpuVramText,
} from "./computeHardwareFormat";
import { formatMemoryForDisplay } from "components/Environment/Create/utils";
import {
  getCloudApplicationAccess,
  getCloudApplicationAccessAudience,
  getCloudComputeActionDisabledReason,
  getCloudComputeCardApplications,
  getPrimaryCloudComputeApplicationAction,
  hasCloudComputeSshApplication,
  isCloudComputeActiveState,
  shouldShowCloudComputeApplicationsLoading,
  shouldShowCloudComputeStatusTooltip,
} from "./cloudComputeCardApplications";
import {
  SSH_ACCESS_HASH,
  SSH_ACCESS_NAVIGATION_STATE,
} from "components/Environment/shared/sshAccessNavigation";

const getCloudComputeStarredKey = (
  environment: Pick<Environment, "environment_id" | "name" | "workspaceId">,
) => {
  const key =
    environment.environment_id || environment.workspaceId || environment.name;
  return key ? `cloud:${key}` : undefined;
};

const CloudComputeStatusChargeRow: React.FC<{
  charge: React.ReactNode;
  statusSummary: React.ReactNode;
}> = ({ charge, statusSummary }) => {
  return (
    <div className="flex min-h-6 items-center justify-end gap-2 leading-[24px]">
      {charge}
      <span aria-hidden="true" className="h-4 w-px shrink-0 bg-[#2e2e2e]" />
      {statusSummary}
    </div>
  );
};

const getCloudGpuVramLabel = (environment: Environment): string | undefined => {
  const topLevelGpu = environment.instance_type?.supported_gpus?.[0];
  const nestedGpu =
    environment.instance?.instance_type_info?.supported_gpus?.[0];

  return (
    formatGpuVramFromByteValue(topLevelGpu?.memory_bytes) ??
    formatGpuVramFromByteValue(nestedGpu?.memory_bytes) ??
    formatGpuVramText(topLevelGpu?.memory) ??
    formatGpuVramText(nestedGpu?.memory)
  );
};

export const getCloudComputeResponsiveHardwareLabel = ({
  gpuCount,
  gpuMemory,
  gpuName,
}: {
  gpuCount?: number;
  gpuMemory?: string;
  gpuName?: string;
}) => {
  if (!gpuName) return "No GPU available";

  return `${gpuCount || 1} x ${gpuName} ${gpuMemory ? `${gpuMemory} VRAM` : "GPU"}`;
};

const CloudCpuHardwareSummary: React.FC<{
  cpuCount?: number;
  memory?: string;
  responsive?: boolean;
}> = ({ cpuCount, memory, responsive = false }) => (
  <div
    className={`flex min-w-0 flex-col text-sm leading-[1.25] text-zinc-500 ${
      responsive
        ? "items-start"
        : "h-full w-full items-center justify-center px-4 py-2 text-center"
    }`}
  >
    <span
      className={`flex lg:flex-col lg:items-center w-fit max-w-full flex-wrap items-baseline gap-x-2 ${
        responsive ? "justify-start" : "justify-center"
      }`}
    >
      <span className="whitespace-nowrap lg:text-base lg:font-black leading-[1.5] text-zinc-400">
        {cpuCount ?? "—"} CPUs
      </span>
      <span className="inline lg:hidden">•</span>
      <span className="whitespace-nowrap font-semibold text-zinc-400">
        {memory || "—"} RAM
      </span>
      <span className="inline lg:hidden">•</span>
      <span className="text-[12px] text-zinc-400">no GPU available</span>
    </span>
  </div>
);

export const CloudComputeCard: React.FC<{ environment: Environment }> = ({
  environment,
}) => {
  const history = useHistory();
  const orgContext = useContext(OrgContext);
  const { me } = useContext(UserContext);
  const workspaceContext = useContext(WorkspaceContext);
  const status = getEnvironmentStatus(environment);
  const statusLabel = getEnvironmentStatusLabel(environment);
  const statusSummaryStatus =
    normalizeEnvironmentStatus(environment.status) ??
    (!environment.instance ? EnvironmentStatus.INSTANCE_LOST : status);
  const isInstanceLost =
    statusSummaryStatus === EnvironmentStatus.INSTANCE_LOST;
  const gpuName = getGpuNameFromEnvironment(environment);
  const gpuCount = getGpuCountFromEnvironment(environment);
  const gpuMemory = getCloudGpuVramLabel(environment);
  const cpuCount = getVcpuFromEnvironment(environment);
  const cpuMemoryValue = getMemoryFromEnvironment(environment);
  const cpuMemory =
    cpuMemoryValue === undefined
      ? undefined
      : formatMemoryForDisplay(cpuMemoryValue);
  const {
    environmentNetworkInfo,
    httpPorts,
    tcpPorts,
    isLoadingPorts,
    isLoadingNetworkInfo,
    isNetworkInfoError,
    resolveCtaHostname,
    setHTTPPortAccess,
    setNetworkMemberType,
    canManagePort,
  } = useEnvironmentNetworkAccess(environment);
  const applications = useMemo(
    () => getCloudComputeCardApplications(environment, httpPorts),
    [environment, httpPorts],
  );
  const sshEnabled = hasCloudComputeSshApplication(tcpPorts);
  const activeState = isCloudComputeActiveState(status);
  const inactiveApplicationActionReason = getCloudComputeActionDisabledReason(
    status,
    "Application",
    statusLabel,
  );
  const inactiveSshActionReason = getCloudComputeActionDisabledReason(
    status,
    "SSH",
    statusLabel,
  );
  const sshControlsDisabledReason = sshEnabled
    ? inactiveSshActionReason
    : (inactiveSshActionReason ?? SSH_NOT_ENABLED_REASON);
  const showApplicationsLoading = shouldShowCloudComputeApplicationsLoading(
    environment,
    isLoadingPorts,
  );
  const networkMemberType = resolveNetworkMemberType(
    environmentNetworkInfo?.networkMemberType,
  );
  const isSkybridge = environment.labels?.["tunnel-provider"] === "skybridge";
  const activeUserIdentities = getCurrentUserAccessIdentities(me);
  const setupIndicatorSteps = getEnvironmentSetupSteps(environment).filter(
    (step) => step.label !== "Ready",
  );
  const starredComputeKey = getCloudComputeStarredKey(environment);
  const launchableId = environment.labels?.launchableId || "";
  const { data: launchable } = useLaunchableQuery(
    launchableId,
    "authenticated",
  );
  const createdAt = parseTimestamp(environment.create_time);
  const canShareSecureLinks = canShareSecureLinkForCompute({
    activeOrgRole: orgContext.activeOrgRole,
    currentUserId: me?.id,
    creatorUserId: environment.labels?.userId,
  });
  const [copiedCommand, setCopiedCommand] = useState<"ssh" | null>(null);
  const [pendingAction, setPendingAction] = useState<
    "start" | "stop" | "delete" | null
  >(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showSimpleStopModal, setShowSimpleStopModal] = useState(false);
  const { storageCostPerHr, isLoadingCreatorInfo, creator } =
    useStorageCostPerHr(environment);
  const ownerName = creator?.name || creator?.email || "";
  const isStopModalDismissed =
    localStorage.getItem(STORAGE_KEYS.stopFreeTierModalDismissed) === "true" &&
    storageCostPerHr === 0;
  const [editNetworkModeOpen, setEditNetworkModeOpen] = useState(false);
  const [sharingPort, setSharingPort] = useState<DisplayPort | null>(null);
  const open = () =>
    history.push(
      `/org/${orgContext.activeOrgId}/environments/${environment.workspaceId}`,
    );
  const openSshAccess = () =>
    history.push({
      pathname: `/org/${orgContext.activeOrgId}/environments/${environment.workspaceId}`,
      hash: SSH_ACCESS_HASH,
      state: SSH_ACCESS_NAVIGATION_STATE,
    });
  const isStoppable = environment.instance?.is_stoppable === true;
  const canStop =
    isStoppable &&
    (environment.status
      ? canRequestEnvironmentStop(environment.status)
      : status === WorkspaceStatus.Running);
  const canStart = statusLabel === "Stopped";
  const deleteBlockedByStatus = isEnvironmentDeleteBlockedByStatus(environment);
  const deleteDisabled = deleteBlockedByStatus || pendingAction !== null;
  const indicatorsDisabled =
    status === WorkspaceStatus.Stopped ||
    status === WorkspaceStatus.Deleting ||
    environment.instance?.status?.lifecycle_status === "terminating" ||
    environment.instance?.status?.lifecycle_status === "terminated";
  const showStatusTooltip = shouldShowCloudComputeStatusTooltip(status);

  const copySshCommand = async () => {
    const organization =
      orgContext.activeOrg?.name?.replace(/ /g, "\\\\ ") || "<org-name>";
    const environmentName =
      environment.name || environment.workspaceId || "environment";
    await navigator.clipboard.writeText(
      `brev shell ${environmentName} --org ${organization}`,
    );
    setCopiedCommand("ssh");
    toast.success("CLI command to SSH copied to the clipboard.");
    window.setTimeout(() => setCopiedCommand(null), 2000);
  };
  const runAction = async (action: "start" | "stop" | "delete") => {
    if (!environment.workspaceId) return;
    if (action === "delete" && deleteBlockedByStatus) return;
    setPendingAction(action);
    try {
      const res =
        action === "start"
          ? await workspaceContext.startWorkspace(environment.workspaceId)
          : action === "stop"
            ? await workspaceContext.stopWorkspace(environment.workspaceId)
            : await workspaceContext.deleteWorkspace(environment.workspaceId);
      if (res.success) {
        toast.success(
          action === "delete"
            ? "Deleting instance"
            : action === "stop"
              ? "Instance is stopping"
              : "Instance is starting",
        );
      } else {
        toast.error(`Unable to ${action} instance`, {
          description: res.message,
        });
      }
    } catch {
      toast.error(`Unable to ${action} instance`);
    } finally {
      setPendingAction(null);
    }
  };
  const shareSecureLink = async (
    authorizedEmails: string[],
    allowPublic: boolean,
  ) => {
    if (!sharingPort) return;
    await setHTTPPortAccess(sharingPort.portId, authorizedEmails, allowPublic);
  };

  const metadata: ComputeCardMetadata[] = [
    {
      unstyled: true,
      value: <ComputeCardKindLabel kind="cloud" />,
    },
    {
      unstyled: true,
      value: (
        <ComputeNetworkMode
          memberType={isSkybridge ? networkMemberType : undefined}
          loading={isSkybridge && isLoadingNetworkInfo}
        />
      ),
    },
    {
      label: "User",
      standalonePill: true,
      value: <ComputeCardOwner userId={environment.labels?.userId} />,
    },
    ...(createdAt
      ? [
          {
            label: "Created",
            standalonePill: true,
            value: <ComputeCardRelativeTime date={createdAt} />,
          },
        ]
      : []),
  ];
  const applicationActionItems = applications.map((application) => {
    const hostname = resolveCtaHostname(application);
    const port = httpPorts.find((httpPort) => httpPort.hostname === hostname);
    const access = getCloudApplicationAccess(
      application,
      port,
      activeUserIdentities,
    );
    const canSharePort =
      !!port &&
      canManagePort(port) &&
      canShareSecureLinks &&
      status !== WorkspaceStatus.Deleting;
    return {
      access,
      accessAudience: getCloudApplicationAccessAudience(application, port),
      application,
      canSharePort,
      hostname,
      port,
    };
  });
  const primaryApplicationAction = getPrimaryCloudComputeApplicationAction(
    applicationActionItems,
    showApplicationsLoading,
  );
  const primaryApplicationSharePort =
    primaryApplicationAction?.canSharePort && primaryApplicationAction.port
      ? primaryApplicationAction.port
      : undefined;
  const statusSummary = (
    <CloudComputeStatusSummary
      indicatorsDisabled={indicatorsDisabled}
      setupIndicatorSteps={setupIndicatorSteps}
      status={statusSummaryStatus}
    />
  );
  const hardwareDetail = (
    <span className="truncate">{gpuMemory ? `${gpuMemory} VRAM` : "GPU"}</span>
  );

  return (
    <>
      <ComputeCardTemplate
        compactAtMedium
        onOpen={isInstanceLost ? undefined : open}
        title={
          <span className={activeState ? undefined : "opacity-50"}>
            {environment.name}
          </span>
        }
        starredComputeKey={starredComputeKey}
        secondaryTitle={
          launchable?.name ? (
            <span className="inline-flex min-w-0 items-center gap-1 text-sm leading-none">
              <span className="shrink-0">from</span>
              <a
                href={`/launchable/deploy?launchableID=${encodeURIComponent(launchableId)}`}
                title={`Open ${launchable.name} launchable`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  history.push(
                    `/launchable/deploy?launchableID=${encodeURIComponent(launchableId)}`,
                  );
                }}
                className="underline decoration-zinc-600 underline-offset-2 transition-colors hover:text-highlight hover:decoration-highlight"
              >
                {launchable.name}
              </a>
            </span>
          ) : undefined
        }
        secondaryTitleClassName="max-md:ml-0 max-md:basis-full"
        metadata={metadata}
        hardware={
          environment.instance ? (
            gpuName ? (
              <ComputeHardwareSummary
                align="center"
                count={gpuCount || 1}
                detail={hardwareDetail}
                name={gpuName}
              />
            ) : (
              <CloudCpuHardwareSummary cpuCount={cpuCount} memory={cpuMemory} />
            )
          ) : undefined
        }
        responsiveHardware={
          environment.instance ? (
            gpuName ? (
              <ComputeHardwareInlineSummary>
                {getCloudComputeResponsiveHardwareLabel({
                  gpuCount,
                  gpuMemory,
                  gpuName,
                })}
              </ComputeHardwareInlineSummary>
            ) : (
              <CloudCpuHardwareSummary
                cpuCount={cpuCount}
                memory={cpuMemory}
                responsive
              />
            )
          ) : undefined
        }
        status={
          <CloudComputeStatusChargeRow
            charge={
              <EnvironmentCostPanel
                appearance="compact"
                environment={environment}
                tooltipDisabled={isInstanceLost}
              />
            }
            statusSummary={
              showStatusTooltip ? (
                <span
                  className="inline-flex items-center align-middle leading-[24px]"
                  onClick={(event) => event.stopPropagation()}
                >
                  <EnvironmentStatusStepper
                    environment={environment}
                    statusLabel={statusLabel}
                    triggerClassName="inline-flex items-center align-middle leading-[24px]"
                  >
                    {statusSummary}
                  </EnvironmentStatusStepper>
                </span>
              ) : (
                statusSummary
              )
            }
          />
        }
        onCopySsh={() => void copySshCommand()}
        sshControlsDisabledReason={sshControlsDisabledReason}
        copiedControl={copiedCommand}
        copySuccessClassName="text-highlight"
        primaryAction={
          primaryApplicationAction ? (
            <EnvironmentApplicationChip
              application={primaryApplicationAction.application}
              hostname={primaryApplicationAction.hostname}
              environmentName={environment.name}
              access={primaryApplicationAction.access}
              accessAudience={primaryApplicationAction.accessAudience}
              disabledReason={inactiveApplicationActionReason}
              onShare={
                primaryApplicationSharePort
                  ? () => setSharingPort(primaryApplicationSharePort)
                  : undefined
              }
            />
          ) : undefined
        }
        menu={
          <ComputeCardMenu label="Instance actions">
            <DropdownMenuLabel>Access</DropdownMenuLabel>
            <DropdownMenuItem
              disabled={Boolean(inactiveSshActionReason)}
              onSelect={
                inactiveSshActionReason === undefined
                  ? openSshAccess
                  : undefined
              }
            >
              Edit SSH access
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Network</DropdownMenuLabel>
            <DropdownMenuItem
              disabled={
                !isOrgAdminOrMaintainerRole(orgContext.activeOrgRole) ||
                !isSkybridge ||
                !environment.environment_id ||
                isLoadingNetworkInfo ||
                isNetworkInfoError
              }
              onSelect={() => setEditNetworkModeOpen(true)}
            >
              Edit Network Mode
            </DropdownMenuItem>
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Instance</DropdownMenuLabel>
              {canStop ? (
                <DropdownMenuItem
                  disabled={pendingAction !== null || isLoadingCreatorInfo}
                  onSelect={() =>
                    isStopModalDismissed
                      ? setShowSimpleStopModal(true)
                      : setShowStopModal(true)
                  }
                >
                  Stop
                </DropdownMenuItem>
              ) : null}
              {canStart ? (
                <DropdownMenuItem
                  disabled={pendingAction !== null}
                  onSelect={() => void runAction("start")}
                >
                  Start
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                disabled={deleteDisabled}
                className={deleteDisabled ? undefined : "text-red-400"}
                onSelect={() => {
                  if (!deleteDisabled) setShowDeleteModal(true);
                }}
              >
                Delete
              </DropdownMenuItem>
            </>
          </ComputeCardMenu>
        }
      />
      <EditNetworkModeDialog
        name={environment.name || environment.workspaceId || "environment"}
        memberType={networkMemberType}
        open={editNetworkModeOpen}
        onOpenChange={setEditNetworkModeOpen}
        onSetMode={setNetworkMemberType}
      />
      <StopModal
        show={showStopModal}
        setShow={setShowStopModal}
        onStop={() => void runAction("stop")}
        storageCostPerHr={storageCostPerHr}
      />
      <Modal
        isOpen={showSimpleStopModal}
        setOpen={setShowSimpleStopModal}
        onClose={() => setShowSimpleStopModal(false)}
        title="Stop Environment"
        hideIcon
        body={
          <p className="text-sm text-secondary">
            Stopping this environment will stop compute charges, but{" "}
            {storageCostPerHr > 0
              ? `$${storageCostPerHr.toFixed(2)}/hr in `
              : ""}
            storage charges will continue. Your data will be preserved.
          </p>
        }
        cancelLabel="Cancel"
        confirmLabel="Confirm Stop"
        onSuccess={() => void runAction("stop")}
      />
      <DeleteConfirmModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onDelete={() => void runAction("delete")}
        resourceName={environment.name || environment.workspaceId || ""}
        orgName={
          orgContext.orgs.find(
            (org) => org.id === environment.labels?.organizationId,
          )?.name
        }
        ownerName={ownerName}
        ownerLoading={isLoadingCreatorInfo && !!environment.labels?.userId}
      />
      {sharingPort ? (
        <ShareSecureLinkDialog
          open={sharingPort !== null}
          onOpenChange={(open) => {
            if (!open) setSharingPort(null);
          }}
          port={sharingPort}
          onSubmit={shareSecureLink}
        />
      ) : null}
    </>
  );
};
